import { useEffect, useMemo, useState } from 'react';
import type { ProspectLoan } from '../../types';
import { extractEncompassLoCompBps } from '@/utils/loCompDerived';
import { getLoCompCapData, type LoCompCapData } from '@/services/loCompCapService';

export interface BpcCap {
  /** When false, BPC input is locked in the aside; tooltip shows `reason`. */
  allowEdit: boolean;
  /** Maximum allowed BPC percent (e.g. 2.75 = 2.75%). Null when !allowEdit or still loading. */
  capPercent: number | null;
  /** Human-readable explanation for the UI. */
  reason: string;
  /** True while the underlying plan data is still loading (first render after mount). */
  isLoading: boolean;
}

/**
 * Client-side BPC cap gate for pricing-v2.
 *
 * Rule (must match the server-side re-validation in the save-group Edge Function):
 *   1. Encompass LO comp margin present on the loan → RESPA dual-comp. Block edits.
 *   2. Else use the assigned LO's optimal_blue_current_percentage as the cap.
 *   3. Else fall back to banked_compensation.
 *   4. Else no plan configured → block edits.
 *
 * Server path re-runs the same rule using the loan's assigned LO (not the acting
 * user) before mutating anything. This hook's result is UX-only — never trust the
 * client gate as the authoritative check.
 */
export function useBpcCap(loan: ProspectLoan, loanOfficerId: string | null): BpcCap {
  const encompassLoCompBps = useMemo(
    () => extractEncompassLoCompBps(loan.rate_lock_buy_side_adjustments),
    [loan.rate_lock_buy_side_adjustments]
  );

  const [capData, setCapData] = useState<LoCompCapData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!loanOfficerId);

  useEffect(() => {
    let cancelled = false;
    if (!loanOfficerId) {
      setCapData(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    getLoCompCapData(loanOfficerId).then((data) => {
      if (!cancelled) {
        setCapData(data);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loanOfficerId]);

  if (encompassLoCompBps > 0) {
    return {
      allowEdit: false,
      capPercent: null,
      reason: 'RESPA dual-comp — remove LO comp margin row in Encompass before editing BPC',
      isLoading: false
    };
  }

  if (!loanOfficerId) {
    return {
      allowEdit: false,
      capPercent: null,
      reason: 'No loan officer assigned to this loan',
      isLoading: false
    };
  }

  if (isLoading || !capData) {
    return {
      allowEdit: false,
      capPercent: null,
      reason: 'Loading comp plan…',
      isLoading: true
    };
  }

  // Phase 2 QA Fix 4 — partner/shareholder exemption. Runs AFTER the RESPA check
  // above (RESPA wins regardless of exemption). When exempt, there is no comp-plan
  // cap to enforce; BPC is freely editable (server mirrors this logic before any
  // DB mutation in validateBpcCap).
  if (capData.isExempt) {
    return {
      allowEdit: true,
      capPercent: null,
      reason: 'Partner — comp plan cap does not apply',
      isLoading: false
    };
  }

  const cap = capData.optimalBluePct ?? capData.bankedCompensationPct ?? null;
  if (cap === null) {
    return {
      allowEdit: false,
      capPercent: null,
      reason: 'No comp plan configured for this loan officer',
      isLoading: false
    };
  }

  return {
    allowEdit: true,
    capPercent: cap,
    reason: `Max BPC plan ${cap.toFixed(2)}%`,
    isLoading: false
  };
}
